import { getSocket } from './client';
import useMessageStore from '../stores/messageStore';
import useChannelStore from '../stores/channelStore';
import usePresenceStore from '../stores/presenceStore';
import useNotificationStore from '../stores/notificationStore';
import useUserStore from '../stores/userStore';
import useAuthStore from '../stores/authStore';
import useReadReceiptStore from '../stores/readReceiptStore';

let registered = false;

export function registerHandlers() {
  const socket = getSocket();
  if (!socket || registered) return;
  registered = true;

  const messageStore = useMessageStore.getState;
  const channelStore = useChannelStore.getState;
  const presenceStore = usePresenceStore.getState;
  const notificationStore = useNotificationStore.getState;
  const userStore = useUserStore.getState;
  const authStore = useAuthStore.getState;
  const readReceiptStore = useReadReceiptStore.getState;

  // ── Messages ──
  socket.on('message:new', (message) => {
    const contextId = message.channelId || message.channel_id || message.dmId || message.dm_conversation_id;
    messageStore().appendMessage(contextId, message);

    // Update unread counts if the message is not from us (or is a system/webhook message)
    const currentUser = authStore().user;
    const senderId = message.userId || message.user_id;
    const msgType = message.messageType || message.message_type;
    const isFromOther = senderId !== currentUser?.id;
    const isSystemMsg = msgType === 'system';

    if (currentUser && (isFromOther || isSystemMsg)) {
      const activeChannelId = channelStore().activeChannelId;
      if (contextId && contextId !== activeChannelId) {
        const dmId = message.dmId || message.dm_conversation_id;
        if (dmId) {
          notificationStore().incrementDmUnread(dmId);
        } else {
          notificationStore().incrementUnread(contextId);
          channelStore().incrementUnread(contextId);
        }
        // Track mentions
        if (
          message.mentions &&
          message.mentions.includes(currentUser.id)
        ) {
          notificationStore().incrementMention(contextId);
        }

        // Browser notification when tab is not focused
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted' && !document.hasFocus()) {
          const sender = message.display_name || message.username || 'New message';
          const body = message.content ? message.content.slice(0, 200) : '';
          new Notification(sender, { body, icon: '/favicon.png', tag: message.id });
        }
      }
    }
  });

  socket.on('message:updated', ({ messageId, channelId, updates }) => {
    const contextId = channelId;
    messageStore().updateMessage(contextId, messageId, updates);
  });

  socket.on('message:deleted', ({ messageId, channelId }) => {
    const contextId = channelId;
    messageStore().removeMessage(contextId, messageId);
  });

  socket.on('message:reaction', ({ messageId, channelId, reactions }) => {
    messageStore().updateMessage(channelId, messageId, { reactions });
  });

  // ── DM Messages ──
  socket.on('dm:new', (message) => {
    const contextId = message.dmId || message.dm_conversation_id;
    messageStore().appendMessage(contextId, message);

    const currentUser = authStore().user;
    const senderId = message.userId || message.user_id;
    // Don't bump the unread badge for a DM the user is currently viewing (mirrors the
    // channel message:new handler). activeChannelId holds the open DM's id.
    const activeId = channelStore().activeChannelId;
    if (currentUser && senderId !== currentUser.id && contextId !== activeId) {
      notificationStore().incrementDmUnread(contextId);
    }
  });

  // ── Presence ──
  socket.on('presence:changed', ({ userId, status, customText, emoji }) => {
    presenceStore().updatePresence(userId, { status, customText, emoji });

    if (status === 'online') {
      userStore().setOnline(userId);
    } else if (status === 'offline') {
      userStore().setOffline(userId);
    }
  });

  socket.on('presence:bulk', (entries) => {
    presenceStore().bulkUpdatePresence(entries);
    entries.forEach(({ userId, status }) => {
      if (status === 'online') {
        userStore().setOnline(userId);
      } else if (status === 'offline') {
        userStore().setOffline(userId);
      }
    });
  });

  // ── Typing ──
  socket.on('typing:update', ({ contextId, userId, isTyping }) => {
    if (isTyping) {
      presenceStore().setTyping(contextId, userId);
    } else {
      presenceStore().clearTyping(contextId, userId);
    }
  });

  // ── Channels ──
  socket.on('channel:created', (channel) => {
    channelStore().addChannel(channel);
  });

  socket.on('channel:updated', ({ channelId, updates }) => {
    channelStore().updateChannel(channelId, updates);
  });

  socket.on('channel:deleted', ({ channelId }) => {
    channelStore().removeChannel(channelId);
  });

  socket.on('channel:member_added', ({ channelId, user }) => {
    // Refresh channel data if needed
    userStore().setUsers([user]);
  });

  socket.on('channel:member_removed', ({ channelId, userId }) => {
    const currentUser = authStore().user;
    if (currentUser && userId === currentUser.id) {
      channelStore().removeChannel(channelId);
    }
  });

  // ── Read Receipts ──
  socket.on('read_receipt:update', ({ channelId, userId, messageId, readAt }) => {
    // Record this member's read pointer so their reader avatar can move to it.
    readReceiptStore().setChannelRead(channelId, userId, messageId, readAt);

    // If it's our own read, also clear our unread badge for the channel.
    const currentUser = authStore().user;
    if (currentUser && userId === currentUser.id) {
      notificationStore().resetUnread(channelId);
      channelStore().resetUnread(channelId);
    }
  });

  socket.on('dm:read', ({ dmConversationId, userId, messageId, readAt }) => {
    readReceiptStore().setDmRead(dmConversationId, userId, messageId, readAt);
  });

  // ── User Updates ──
  socket.on('user:updated', ({ userId, updates }) => {
    userStore().updateUser(userId, updates);

    // If it's us, update auth store too
    const currentUser = authStore().user;
    if (currentUser && userId === currentUser.id) {
      authStore().updateUser(updates);
    }
  });

  // ── Notification Count Sync ──
  socket.on('unread:sync', ({ channelId, count, mentionCount }) => {
    notificationStore().setUnread(channelId, count);
    if (mentionCount !== undefined) {
      notificationStore().setMention(channelId, mentionCount);
    }
  });
}

export function unregisterHandlers() {
  const socket = getSocket();
  if (socket) {
    socket.off('message:new');
    socket.off('message:updated');
    socket.off('message:deleted');
    socket.off('message:reaction');
    socket.off('dm:new');
    socket.off('presence:changed');
    socket.off('presence:bulk');
    socket.off('typing:update');
    socket.off('channel:created');
    socket.off('channel:updated');
    socket.off('channel:deleted');
    socket.off('channel:member_added');
    socket.off('channel:member_removed');
    socket.off('read_receipt:update');
    socket.off('dm:read');
    socket.off('user:updated');
    socket.off('unread:sync');
  }
  registered = false;
}
